import React, { useEffect, useState } from "react";
import { Spin, Table, Tag, Badge } from "antd";
import { getDoctorDashboard } from "../../apis/roleDashboard";

interface OPDPatient {
  id: number;
  patientName: string;
  patientPhone: string | null;
  time: string | null;
  chiefComplaint: string | null;
  status: string | null;
  visitNumber: string | null;
}

interface IPDPatient {
  id: number;
  patientName: string;
  room: string;
  admittedOn: string | null;
  days: number | null;
  status: string | null;
  diagnosis: string | null;
}

interface OTEntry {
  id: number;
  visitId: number;
  patientName: string;
  procedure: string;
  time: string | null;
  status: string | null;
}

interface DoctorData {
  opdToday: OPDPatient[];
  ipdActive: IPDPatient[];
  otToday: OTEntry[];
}

const DoctorDashboard: React.FC = () => {
  const [data, setData] = useState<DoctorData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDoctorDashboard()
      .then((res: any) => setData(res.data.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center mt-16"><Spin size="large" /></div>;
  if (!data) return <div className="text-gray-400 mt-8 text-center">Failed to load dashboard.</div>;

  const opdColumns = [
    { title: "Time", dataIndex: "time", key: "time", width: 70, render: (v: string) => v ?? "—" },
    { title: "Patient", dataIndex: "patientName", key: "patientName" },
    { title: "Phone", dataIndex: "patientPhone", key: "patientPhone", render: (v: string) => v ?? "—" },
    { title: "Complaint", dataIndex: "chiefComplaint", key: "chiefComplaint", render: (v: string) => v ?? "—" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: string) => v ? <Tag>{v}</Tag> : null,
    },
  ];

  const ipdColumns = [
    { title: "Patient", dataIndex: "patientName", key: "patientName" },
    { title: "Room", dataIndex: "room", key: "room" },
    { title: "Day", dataIndex: "days", key: "days", width: 60, render: (v: number) => <Badge count={v} color={v > 7 ? "red" : "blue"} /> },
    { title: "Admitted", dataIndex: "admittedOn", key: "admittedOn" },
    { title: "Diagnosis", dataIndex: "diagnosis", key: "diagnosis", render: (v: string) => v ?? "—" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: string) => v ? <Tag color={v?.toLowerCase().includes("discharge") ? "orange" : "green"}>{v}</Tag> : null,
    },
  ];

  const otColumns = [
    { title: "Time", dataIndex: "time", key: "time", width: 70, render: (v: string) => v ?? "—" },
    { title: "Patient", dataIndex: "patientName", key: "patientName" },
    { title: "Procedure", dataIndex: "procedure", key: "procedure" },
    { title: "Status", dataIndex: "status", key: "status", render: (v: string) => v ? <Tag>{v}</Tag> : null },
  ];

  return (
    <div className="space-y-6">
      {/* Summary chips */}
      <div className="flex gap-4">
        <div className="bg-white rounded-lg p-4 shadow-sm border flex-1 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide">OPD Today</p>
          <p className="text-3xl font-bold text-blue-600">{data.opdToday.length}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border flex-1 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Active IPD</p>
          <p className="text-3xl font-bold text-purple-600">{data.ipdActive.length}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border flex-1 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide">OT Today</p>
          <p className="text-3xl font-bold text-orange-500">{data.otToday.length}</p>
        </div>
      </div>

      {/* Today's OPD */}
      <div className="bg-white rounded-lg p-4 shadow-sm border">
        <h3 className="font-semibold text-gray-700 mb-3">Today's OPD Patients</h3>
        <Table
          dataSource={data.opdToday}
          columns={opdColumns}
          rowKey="id"
          size="small"
          pagination={false}
          locale={{ emptyText: "No OPD patients today" }}
        />
      </div>

      {/* Active IPD */}
      <div className="bg-white rounded-lg p-4 shadow-sm border">
        <h3 className="font-semibold text-gray-700 mb-3">Active IPD Patients</h3>
        <Table
          dataSource={data.ipdActive}
          columns={ipdColumns}
          rowKey="id"
          size="small"
          pagination={false}
          locale={{ emptyText: "No active IPD patients" }}
        />
      </div>

      {/* OT today */}
      {data.otToday.length > 0 && (
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <h3 className="font-semibold text-gray-700 mb-3">Today's OT</h3>
          <Table
            dataSource={data.otToday}
            columns={otColumns}
            rowKey="id"
            size="small"
            pagination={false}
          />
        </div>
      )}
    </div>
  );
};

export default DoctorDashboard;
